import { Col, InputGroup, Row, Table } from "react-bootstrap";
import {
    AutoCol,
    Checkbox,
    DateInput,
    DateTimeInputs,
    FormCard,
    FormLabelText,
    Input,
    RadioGroup,
    RadioOption,
    Select,
    Textarea,
} from "../../../component-library";
import { capitalizeFirst } from "../../../helper-functions";
import { useCurrentCase, useReferralFormData } from "../../../hooks";
import { RecoveryAppFormData, SelectOption } from "../../../models";
import { YesNoRadioOptions } from "../../../models/form/RadioOptions";
import { RenderedFormProps } from "../RenderForm";
import { DaughterTissuesList, SuppliesList } from ".";

export interface RecoveryFormData extends Partial<RecoveryAppFormData> {
    [key: string]: any;
}

const yesNoOptions = YesNoRadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) })); //yes/no values from Recovery data are capitalized

const cardiacRadioOptions = ["Cardiac", "Cross Clamp", "LSA"].map<RadioOption>((o) => ({ value: o, label: o }));

const idOptions = ["Wrist Band", "Ankle Band", "Toe Tag", "Other"];

const tissuesProcuredOptions = ["OD", "OS", "Both"].map<RadioOption>((o) => ({ value: o, label: o }));

const corneaOptions = ["Abnormal", "Normal"].map<RadioOption>((o) => ({ value: o, label: o }));

const conjOptions = ["Normal", "Edema", "Bloodshot", "Pterygium"];

const scleraOptions = ["Normal", "Jaundice", "Petechiae"];

const lensOptions = ["Phakic (Natural)", "Pseudophakic (IOL)", "Aphakic"].map<RadioOption>((o) => ({
    value: o,
    label: o,
}));

const weightOptions = ["lbs", "Kg"].map<RadioOption>((o) => ({ value: o, label: o }));

const verifiedViaOptions = ["Medical Records", "Physical Assessment", "ADFS"].map<RadioOption>((o) => ({
    value: o,
    label: o,
}));

const eligibilityOptions = ["Eligible", "Ineligible"].map<RadioOption>((o) => ({ value: o, label: o }));

const procedureTypeOptions = ["Excision", "Enucleation"].map<SelectOption>((o) => ({ value: o, label: o }));

const ocularCoolingOptions = ["Ocular Prep", "Cool Storage"].map<RadioOption>((o) => ({ value: o, label: o }));

export function RecoveryForm({ formState }: Readonly<RenderedFormProps>) {
    const { isReadonly } = formState;
    const formData: RecoveryFormData = formState.formData?.data ?? {};
    const { referral, case: _case } = useCurrentCase({ includeDetails: true });
    const {
        donorAge,
        donorDOB,
        donorFirstLastName,
        donorGender,
        donorMrn,
        donorRace,
        hospitalName,
        referringOrganizationUnitName,
        referringOrganizationUnitRoom,
        donorAttendingMd,
        donorTimeOfDeathDateAndTime,
        donorTimeOfDeathTimezone,
        donorAdmissionDate,
        donorAdmissionTime,
        donorWeight,
        donorWeightUnit,
    } = useReferralFormData(referral);

    return (
        <>
            <h1>Recovery Form</h1>
            <h3>
                <b>Donor Information</b>
            </h3>
            <Row>
                <AutoCol>
                    <h3>Donor Name:</h3>
                </AutoCol>
                <Col>{donorFirstLastName}</Col>
                <AutoCol>
                    <h3>Original Donor Location / Unit:</h3>
                </AutoCol>
                <AutoCol>{hospitalName}</AutoCol>
                <Col>
                    {referringOrganizationUnitName} {referringOrganizationUnitRoom}
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Consent was verified and found to be valid:"
                        accessor="MHRConsentValid"
                        options={yesNoOptions}
                    />
                </AutoCol>
                <AutoCol>
                    <DateTimeInputs
                        inlineLabel
                        label="Date & Time verified:"
                        dateAccessor="MHRConsentValidDate"
                        dateFormat="yyyy-MM-dd"
                        timeAccessor="MHRConsentValidTime"
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <h3>Age:</h3>
                </AutoCol>
                <AutoCol>{donorAge}</AutoCol>
                <AutoCol>
                    <h3>Race:</h3>
                </AutoCol>
                <AutoCol>{donorRace}</AutoCol>
                <AutoCol>
                    <h3>Gender:</h3>
                </AutoCol>
                <AutoCol>{donorGender}</AutoCol>
                <AutoCol>
                    <h3>Date of Birth:</h3>
                </AutoCol>
                <AutoCol>{donorDOB}</AutoCol>
                <AutoCol>
                    <Row>
                        <AutoCol>
                            <Checkbox accessor="donorMRN" label="MR#" />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="donorNA" label="N/A" />
                        </AutoCol>
                        <AutoCol>{donorMrn}</AutoCol>
                    </Row>
                </AutoCol>
            </Row>
            <h3>
                <b>Medical Record Information</b>
            </h3>
            <Row>
                <Col>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Medical records were reviewed and copied in accordance to the ASN policy and procedure E1.050:"
                        accessor="medicalRecordsReviewed"
                        options={yesNoOptions}
                    />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <FormLabelText
                        label="Date & Time of Arrival/Triage"
                        text1={`${donorAdmissionDate} ${donorAdmissionTime}`}
                    />
                </AutoCol>
                <AutoCol>
                    <FormLabelText
                        label="Date/Time of Death"
                        text1={donorTimeOfDeathDateAndTime}
                        text2={donorTimeOfDeathTimezone}
                    />
                </AutoCol>
                <AutoCol>
                    <RadioGroup accessor="cardiacOption" options={cardiacRadioOptions} />
                </AutoCol>
            </Row>
            <Row>
                <Col>
                    <Input label="Primary Cause of Death:" inlineLabel accessor="PrimaryCauseOfDeath" />
                </Col>

                <Col>
                    <Input label="Secondary Cause of Death:" inlineLabel accessor="SecondaryCauseOfDeath" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <h3>Attending Physician:</h3>
                </AutoCol>
                <Col>{donorAttendingMd}</Col>
                <Col xxl={6}>
                    <Input label="Pronouncing Physician:" inlineLabel accessor="PronouncingPhysician" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Do any of the following conditions apply?"
                        accessor="conditionsApply"
                        options={yesNoOptions}
                    />
                </AutoCol>
                <AutoCol>If yes, check EACH relevant item.</AutoCol>
            </Row>
            <Row>
                <Col>
                    <Checkbox accessor="deathUnknownCause" label="Death of Unknown Cause" />
                    <Checkbox accessor="activeSepticemia" label="Active Septicemia" />
                    <Checkbox accessor="activeEndocarditis" label="Active Endocarditis" />
                    <Checkbox accessor="viralHepatitis" label="History of Viral Hepatitis" />
                    <Checkbox accessor="activeLeukemia" label="Active Leukemia" />
                    <Checkbox accessor="lymphoma" label="Lymphoma" />
                    <Checkbox accessor="multipleMyeloma" label="Multiple Myeloma" />
                    <Checkbox accessor="hivAids" label="HIV/AIDS and Risk Groups" />
                    <Checkbox accessor="activeSyphilis" label="Active Syphilis" />
                    <Checkbox accessor="smallPoxVaccination" label="Smallpox Vaccination in last six weeks" />
                </Col>
                <Col>
                    <Checkbox accessor="rabies" label="Rabies within the past six months" />
                    <Checkbox accessor="documentedDementia" label="Documented Dementia" />
                    <Checkbox accessor="reyesSyndrome" label="Reye's Syndrome within the last three months" />
                    <Checkbox accessor="congenitalRubella" label="Congenital Rubella" />
                    <Checkbox accessor="panencephalitis" label="Subacute Sclerosing Panencephalitis" />
                    <Checkbox accessor="leukoencephalopathy" label="Progressive Multifocal Leukoencephalopathy" />
                    <Checkbox
                        accessor="activeEncephalitis"
                        label="Active viral Encephalitis, encephalitis of unknown origin, or progressive encephalopathy"
                    />
                    <Checkbox
                        accessor="deathNeurologic"
                        label="Death from neurologic disease of unestablished diagnosis or etiology"
                    />
                    <Checkbox accessor="growthHormone" label="Human Pituitary Growth hormone" />
                    <Checkbox
                        accessor="cjd"
                        label="Any form of Creutzfeldt-Jakob Disease or a Blood relative with CJD"
                    />
                    <Checkbox accessor="allograft" label="Xenotransplant Allograft" />
                </Col>
            </Row>
            <Row>
                <AutoCol>Comments:</AutoCol>
            </Row>
            <Row>
                <Col>
                    <Textarea accessor="medicalRecordComments" />
                </Col>
            </Row>

            <SuppliesList formData={formData} />

            <Row>
                <AutoCol>
                    <Checkbox
                        accessor="TrackingDocs"
                        label="Manufacturer's lot numbers and expiration dates were verified, documented and found acceptable for use on critical supplies and the
documentation from the Non-Sterile Supply Pack, Sterile Excision and/or Sterile Enucleation Supplemental Pack have been provided for tracking
purposes in accordance to the ASN policy and procedure E1.050."
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Pre-Recovery Preparation</b>
            </h3>
            <Row>
                <Col>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Before approaching the donor, was all personal protective equipment donned in accordance to the ASN policy and procedure E1.050:"
                        accessor="protectiveEquipmentDonned"
                        options={yesNoOptions}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Verified the name of the donor in accordance with the EBAA Medical Standards D1.000:"
                        accessor="verifiedDonorName"
                        options={yesNoOptions}
                    />
                </Col>
            </Row>
            <Row>
                <Col>Manner of Identification (Check all that apply)</Col>
            </Row>
            <Row>
                {idOptions.map((o, i) => (
                    <AutoCol key={o}>
                        <Checkbox accessor={`Identification.${i}`} label={o} value={o} />
                    </AutoCol>
                ))}
                <AutoCol>
                    <Input label="Name:" inlineLabel accessor="IdentificationName" />
                </AutoCol>
                <AutoCol>
                    <Input label="Title:" inlineLabel accessor="IdentificationTitle" />
                </AutoCol>
                <AutoCol>
                    <DateInput
                        label="Time:"
                        inlineLabel
                        accessor="IdentificationTime"
                        onlyTime
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Penlight Examination</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="Designate, Ocular Tissue Procured:"
                        accessor="EyeProcured"
                        options={tissuesProcuredOptions}
                    />
                </AutoCol>
            </Row>
            <Row>
                <Col>
                    <FormCard title="OD">
                        <Row>
                            <Col>
                                <RadioGroup
                                    inline
                                    inlineGroupName
                                    groupName="Cornea:"
                                    accessor="PenlightCorneaOD"
                                    options={corneaOptions}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <AutoCol>Conjunctiva:</AutoCol>
                            {conjOptions.map((o, i) => (
                                <AutoCol key={o}>
                                    <Checkbox accessor={`PenlightConjunctivaOD.${i}`} label={o} value={o} />
                                </AutoCol>
                            ))}
                        </Row>
                        <Row>
                            <AutoCol>Sclera:</AutoCol>
                            {scleraOptions.map((o, i) => (
                                <AutoCol key={o}>
                                    <Checkbox accessor={`ScleraOD.${i}`} label={o} value={o} />
                                </AutoCol>
                            ))}
                        </Row>
                        <Row>
                            <Col>
                                <RadioGroup
                                    inline
                                    inlineGroupName
                                    groupName="Lens:"
                                    accessor="LensOD"
                                    options={lensOptions}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <AutoCol>Please document any additional findings below:</AutoCol>
                        </Row>
                        <Row>
                            <Col>
                                <Textarea accessor="PenlightCommentsOD" label="Comments:" inlineLabel />
                            </Col>
                        </Row>
                    </FormCard>
                </Col>
                <Col>
                    <FormCard title="OS">
                        <Row>
                            <Col>
                                <RadioGroup
                                    inline
                                    inlineGroupName
                                    groupName="Cornea:"
                                    accessor="PenlightCorneaOS"
                                    options={corneaOptions}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <AutoCol>Conjunctiva:</AutoCol>
                            {conjOptions.map((o, i) => (
                                <AutoCol key={o}>
                                    <Checkbox accessor={`PenlightConjunctivaOS.${i}`} label={o} value={o} />
                                </AutoCol>
                            ))}
                        </Row>
                        <Row>
                            <AutoCol>Sclera:</AutoCol>
                            {scleraOptions.map((o, i) => (
                                <AutoCol key={o}>
                                    <Checkbox accessor={`ScleraOS.${i}`} label={o} value={o} />
                                </AutoCol>
                            ))}
                        </Row>
                        <Row>
                            <Col>
                                <RadioGroup
                                    inline
                                    inlineGroupName
                                    groupName="Lens:"
                                    accessor="LensOS"
                                    options={lensOptions}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <AutoCol>Please document any additional findings below:</AutoCol>
                        </Row>
                        <Row>
                            <Col>
                                <Textarea accessor="PenlightCommentsOS" label="Comments:" inlineLabel />
                            </Col>
                        </Row>
                    </FormCard>
                </Col>
            </Row>
            <h3>
                <b>Specimen Collection Information</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        groupName="A blood specimen was collected:"
                        accessor="MHRBloodCollected"
                        options={yesNoOptions}
                    />
                </AutoCol>
                <AutoCol>
                    <Input
                        inlineLabel
                        label="If Yes, Number of Tubes"
                        accessor="MHRBloodCollectedTubes"
                        type="number"
                    />
                </AutoCol>
                <Col>
                    <Input inlineLabel label="If No, Comment" accessor="MHRBloodCollectedComment" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <Checkbox
                        accessor="PDWPreTrans"
                        value="Pre-Transfusion / Infusion Specimen"
                        label="Pre-Transfusion / Infusion Specimen"
                    />
                </AutoCol>
                <AutoCol>
                    <Checkbox
                        accessor="PDWPreTrans"
                        value="Pre / Post Mortem Specimen"
                        label="Pre / Post Mortem Specimen"
                    />
                </AutoCol>
                <AutoCol>
                    <DateTimeInputs
                        inlineLabel
                        label="Date / Time:"
                        dateFormat="yyyy-MM-dd"
                        dateAccessor="UABCollectDate"
                        timeAccessor="UABCollectTime"
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
                <AutoCol>
                    <Checkbox accessor="specimenCollectionNA" label="N/A" inline />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    Donor's weight: {donorWeight} {donorWeightUnit}
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="verifiedVia"
                        groupName="Verified via:"
                        options={verifiedViaOptions}
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Physical Assessment</b>
            </h3>
            <Row>
                <AutoCol>
                    <u>High Risk Assessment</u>
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="Tattoos" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Tattoo(s)</AutoCol>
                <AutoCol>
                    <i>If Yes:</i>
                </AutoCol>
                <AutoCol className="d-flex flex-column">
                    <Checkbox
                        accessor="TattoosIfYes"
                        label="Professional in appearance"
                        value="Professional in appearance"
                        inline
                    />
                    <Checkbox
                        accessor="TattoosIfYes"
                        label="Non-professional in appearance"
                        value="Not Professional in appearance"
                        inline
                    />
                </AutoCol>
                <Col>
                    <Input accessor="TattoosOther" placeholder="Other" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="Piercings" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Piercing(s)</AutoCol>
                <AutoCol>
                    <i>If Yes:</i>
                </AutoCol>
                <AutoCol className="d-flex flex-column">
                    <Checkbox
                        accessor="PiercingsIfYes"
                        label="Professional in appearance"
                        value="Professional in appearance"
                        inline
                    />
                    <Checkbox
                        accessor="PiercingsIfYes"
                        label="Non-professional in appearance"
                        value="Not Professional in appearance"
                        inline
                    />
                </AutoCol>
                <Col>
                    <Input accessor="PiercingsOther" placeholder="Other" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="NonMedical" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Non-medical injection sites (IV needle/track marks)</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="SypUlsers" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>
                    Physical evidence for risk of, or evidence of syphilis (e.g. chancre, reddish-brown rash with
                    individual lesions of unknown etiology.)
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HIVMaleEvidenceAnalIntercourse" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Male donors, physical evidence of anal intercourse or perianal condyloma.</AutoCol>
                <AutoCol>
                    <Checkbox inline accessor="RFFemaleDonor" value="N/A" label="N/A- Female Donor" />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="PhysicalEvidenceSTD" options={yesNoOptions} />
                </AutoCol>
                <Col className="px-0 mb-2">
                    <Row>
                        <AutoCol>Physical evidence of any sexually transmitted diseases</AutoCol>
                        <AutoCol>
                            (e.g. genital ulcerative disease, herpes simplex, chancroid, etc., these signs should be
                            considered in light of other information obtained about the donor.)
                        </AutoCol>
                    </Row>
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HIVUnixLymphadenopathy" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Unexplained lymphadenopathy (enlarged lymph nodes)</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HIVUnexOralThrush" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Unexplained oral thrush</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HIVKaposisSarcoma" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Blue or purple spots on the skin or mucous membranes typical of Kaposi's sarcoma</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HepUnexJaundice" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Unexplained jaundice (icterus)</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="HepUnexHepatomegaly" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Unexplained hepatomegaly (enlarged liver)</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="SignsOfSepsis" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Physical signs of sepsis (an unexplained general rash)</AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="VaccScab" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>
                    Recent evidence of Smallpox vaccination (e.g. vaccination scab, eczema vaccinatum, vesicular rash or
                    vaccinia necrosum)
                </AutoCol>
            </Row>
            <Row>
                <Col>
                    <Textarea label="If Yes, explain:" accessor="IVComments" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup inline accessor="ItemsOfValue" options={yesNoOptions} />
                </AutoCol>
                <AutoCol>Were there any items of value located on or around the donor?</AutoCol>
            </Row>
            <Row>
                <Col>
                    <Textarea label="If Yes, describe item and how it was secured:" accessor="ItemsOfValueComments" />
                </Col>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="RFDnrEligibleIneligible"
                        options={eligibilityOptions}
                        groupName="I have performed a complete anterior and posterior physical assessment of this donor; and, to the best of my ability, have found the donor to be
for donation prior to recovery based on the physical assessment findings, in accordance of the EBAA Medical Standards D1.000 and the FDA
regulations 1271.75:"
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Recovery Procedure</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="prepScrubPerformed"
                        options={yesNoOptions}
                        groupName="The ocular prep and surgical scrub was performed in accordance to the ASN policy and procedure E1.050:"
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>Procedure Type:</AutoCol>
                <AutoCol>
                    <Select
                        isDisabled={isReadonly}
                        accessor="procedureTypeOD"
                        label="OD:"
                        options={procedureTypeOptions}
                    />
                </AutoCol>
                <AutoCol>
                    <Select
                        isDisabled={isReadonly}
                        accessor="procedureTypeOS"
                        label="OS:"
                        options={procedureTypeOptions}
                    />
                </AutoCol>
                <AutoCol>
                    <InputGroup>
                        <DateTimeInputs
                            label="Date & Time of Procedure"
                            inlineLabel
                            dateAccessor="ProcedureDate"
                            timeAccessor="ProcedureTime"
                            ignoreTimeZone
                            inGroup
                            disabled={isReadonly}
                        />
                        <Select
                            isDisabled={isReadonly}
                            accessor="ProcedureTimeZone"
                            slug="time_zone"
                            inGroup
                            placeholder="time zone"
                        />
                    </InputGroup>
                </AutoCol>
                <AutoCol>
                    <Checkbox label="N/A" accessor="procedureNA" />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <Input label="Location of Procedure:" inlineLabel accessor="procedureLocation" />
                </AutoCol>
                <AutoCol>
                    <Input label="Unit:" inlineLabel accessor="procedureLocationUnit" />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <DateTimeInputs
                        label="Date & Time Ocular Cooling Began:"
                        inlineLabel
                        dateAccessor="OcularCoolingDate"
                        timeAccessor="OcularCoolingTime"
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
                <AutoCol>
                    <Checkbox label="N/A" inline accessor="ocularCoolingNA" />
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        accessor="ocularCoolingMethod"
                        groupName="Method of ocular cooling:"
                        inline
                        inlineGroupName
                        options={ocularCoolingOptions}
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="procedurePolicy"
                        options={yesNoOptions}
                        groupName="The recovery procedure was performed in accordance to the ASN policy and procedure E1.100 / E1.120:"
                    />
                </AutoCol>
            </Row>

            <DaughterTissuesList formState={formState} />

            <h3>
                <b>Departures</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="departures"
                        options={yesNoOptions}
                        groupName="Were there any Departures from the Advancing Sight Network's Standard Operating Procedures?"
                    />
                </AutoCol>
                <AutoCol>
                    <i>If Yes, describe below:</i>
                </AutoCol>
            </Row>
            <Row>
                <Col>
                    <Textarea accessor="departuresComment" />
                </Col>
            </Row>
            <h3>
                <b>OPO Information</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="organConsent"
                        options={yesNoOptions}
                        groupName="Was consent provided for Organ donation?"
                    />
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="organRecovered"
                        options={yesNoOptions}
                        groupName="Were Organs recovered?"
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="tissueConsent"
                        options={yesNoOptions}
                        groupName="Was consent provided for Tissue donation?"
                    />
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="tissueRecovered"
                        options={yesNoOptions}
                        groupName="Were Tissues recovered (or is tissue recovery anticipated)?"
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>OPO Donor #:</AutoCol>
                <AutoCol>{referral?.caseFileIdentifier}</AutoCol>
                <AutoCol>OPO Blood Specimen draw:</AutoCol>
                <AutoCol>
                    <Checkbox label="N/A" inline accessor="opoBloodDrawNA" />
                </AutoCol>
                <AutoCol>
                    <DateTimeInputs
                        label="Date/Time"
                        inlineLabel
                        dateAccessor="opoBloodDrawDate"
                        timeAccessor="opoBloodDrawTime"
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Post Mortem Pathology</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="autopsyRequested"
                        options={yesNoOptions}
                        groupName="Has an Autopsy been requested?"
                    />
                </AutoCol>
                <AutoCol>
                    <Input label="If Yes, Where?" inlineLabel accessor="autopsyLocation" />
                </AutoCol>
                <AutoCol>
                    <Input label="Phone Number" inlineLabel accessor="autopsyPhoneNumber" isPhone />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="meCase"
                        options={yesNoOptions}
                        groupName="Medical Examiner Case?"
                    />
                </AutoCol>
                <AutoCol>
                    <Input accessor="meNumber" label="ME#" inlineLabel />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <Input accessor="permissionBy" label="If Yes, Permission for recovery was given by" inlineLabel />
                </AutoCol>
                <AutoCol>Date/Time</AutoCol>
                <AutoCol>
                    <Checkbox label="N/A" inline accessor="permissionNA" />
                </AutoCol>
                <AutoCol>
                    <DateTimeInputs
                        dateAccessor="permissionDate"
                        timeAccessor="permissionTime"
                        ignoreTimeZone
                        disabled={isReadonly}
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="vitreousDrawn"
                        options={yesNoOptions}
                        groupName="Was Vitreous drawn per ASN policy?"
                    />
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="autopsyDeclined"
                        options={yesNoOptions}
                        groupName="Was Autopsy declined due to a perceived risk of communicable disease?"
                    />
                </AutoCol>
            </Row>
            <h3>
                <b>Post-Recovery Information</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="elevatedHead"
                        options={yesNoOptions}
                        groupName="Elevated head / upper body:"
                    />
                </AutoCol>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="embalerTagLeft"
                        options={yesNoOptions}
                        groupName="Embalmer Alert Tag left with body:"
                    />
                </AutoCol>
            </Row>
        </>
    );
}
